import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSparkles,
  faExternalLinkAlt,
} from '@fortawesome/pro-regular-svg-icons'
import Typography from '@mui/material/Typography'
import {
  LoadingStateComponent,
  useLabraChat,
  GradientFaIcon,
} from '@labrav/common'
import { useNavigate } from 'react-router-dom'
import { FundingEligibilityProgram } from '../../api/fundingApi'
import { formatFundingBand } from '../../utils/format'
import { coSellReferralsLink } from '../../utils/coSellReferralsLink'
import { useStyles } from './FundingEligibilityPrograms.styles'

interface FundingEligibilityProgramsProps {
  programs: FundingEligibilityProgram[]
  loading?: boolean
  currency?: string
  /** Needed to link a program's referral count to the CoSell pipeline. */
  accountId?: string
}

const currencySymbol = (currency: string) =>
  currency === 'USD' ? '$' : currency

const ELIGIBLE_STATUS = 'eligible'

/**
 * A stated prerequisite status wins. When the API sends none, having eligible
 * referrals is itself proof of eligibility — otherwise a partner with matching
 * opportunities would still be asked "am I eligible?".
 */
const isEligible = (program: FundingEligibilityProgram) => {
  if (program.prerequisite_status) {
    return program.prerequisite_status === ELIGIBLE_STATUS
  }
  return program.eligible_referrals_count > 0
}

const whatIsPrompt = (programName: string) => `What is ${programName}?`

const reviewPrompt = (programName: string, eligible: boolean) =>
  eligible
    ? `What all opportunities are eligible for ${programName} funding?`
    : `Am I eligible for ${programName}? What do I need to qualify?`

export const FundingEligibilityPrograms = ({
  programs,
  loading,
  currency = 'USD',
  accountId,
}: FundingEligibilityProgramsProps) => {
  const { classes, cx } = useStyles()
  const navigate = useNavigate()
  const { open, startNewChat, sendPrompt } = useLabraChat()
  const symbol = currencySymbol(currency)

  const askChat = (prompt: string) => {
    open()
    startNewChat()
    sendPrompt(prompt)
  }

  if (loading) {
    return (
      <div className={classes.loader}>
        <LoadingStateComponent
          heading="Fetching your funding eligibility programs"
          description="It may take a few moments, please wait..."
        />
      </div>
    )
  }

  return (
    <div className={classes.list} data-testid="funding-eligibility-programs">
      {programs.map(program => {
        const band = formatFundingBand(program.potential_funding)
        const eligible = isEligible(program)
        const referralsLink =
          accountId && program.eligible_referrals_count > 0
            ? coSellReferralsLink(accountId, program.program_name)
            : null
        return (
          <section key={program.program_name} className={classes.programCard}>
            <div className={classes.titleRow}>
              <Typography
                component="button"
                type="button"
                className={cx(classes.title, classes.titleButton)}
                onClick={() => askChat(whatIsPrompt(program.program_name))}
              >
                {program.program_name}
                {program.external_url && (
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                )}
              </Typography>
              {program.prerequisite_label && (
                <Typography
                  component="span"
                  className={cx(
                    classes.pill,
                    eligible ? classes.pillEligible : classes.pillMissing
                  )}
                >
                  {program.prerequisite_label}
                </Typography>
              )}
            </div>

            {program.description && (
              <Typography component="span" className={classes.description}>
                {program.description}
              </Typography>
            )}

            <div className={classes.metricsRow}>
              <div className={classes.metric}>
                <Typography component="span" className={classes.metricLabel}>
                  Potential funding
                </Typography>
                {band ? (
                  <span className={classes.metricValue}>
                    <Typography
                      component="span"
                      className={classes.metricSymbol}
                    >
                      {symbol}
                    </Typography>
                    <Typography
                      component="span"
                      className={classes.metricAmount}
                    >
                      {band}
                    </Typography>
                  </span>
                ) : (
                  <Typography component="span" className={classes.metricMuted}>
                    -
                  </Typography>
                )}
              </div>

              <div className={classes.metric}>
                <Typography component="span" className={classes.metricLabel}>
                  Eligible referrals
                </Typography>
                {referralsLink ? (
                  <button
                    type="button"
                    className={cx(classes.referralBadge, classes.referralLink)}
                    data-testid={`eligible-referrals-${program.program_name}`}
                    title={`View referrals eligible for ${program.program_name}`}
                    onClick={() => navigate(referralsLink)}
                  >
                    {program.eligible_referrals_count}
                  </button>
                ) : (
                  <Typography
                    component="span"
                    className={classes.referralBadge}
                  >
                    {program.eligible_referrals_count}
                  </Typography>
                )}
              </div>

              <button
                type="button"
                className={classes.reviewBtn}
                onClick={() =>
                  askChat(reviewPrompt(program.program_name, eligible))
                }
              >
                <GradientFaIcon
                  icon={faSparkles}
                  className={classes.reviewIcon}
                />
                Review with AI
              </button>
            </div>
          </section>
        )
      })}
    </div>
  )
}
